"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { setPageTitle } from "@/lib/page-titles";
import { BLOG_POSTS, ALL_POST_SLUGS } from "@/lib/blog-posts";
import { FadeIn } from "@/components/ui/fade-in";
import { ScatterChart } from "@/components/blog/charts";
import { LevelChart } from "@/components/blog/arc-agi-3/LevelChart";
import { LandingFooter } from "@/components/ui/footer";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { PillButton } from "@/components/ui/pill-button";
import { MuxVideo } from "@/components/ui/mux-video";

export default function BlogIndexPage() {
  const isDark = true;
  const router = useRouter();

  useEffect(() => {
    setPageTitle("Blog");
  }, []);

  return (
    <div className="grain min-h-screen bg-[#09090a] text-white font-arizona-sans antialiased selection:bg-[#7678E3]/30 agentica-page-fade-in">
      <SiteNavigation isDark={isDark} activePage="blog" />

      {/* Header */}
      <div className="relative pt-32 pb-16 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1]">
            Blog
          </h1>
        </div>
      </div>

      {/* Blog post cards */}
      <section className="px-6 sm:px-10 pb-32">
        <div className="max-w-7xl mx-auto flex flex-col gap-10">
          {ALL_POST_SLUGS.map((slug) => {
            const post = BLOG_POSTS[slug]!;
            return (
              <FadeIn
                key={slug}
                onClick={() => router.push(`/blog/${slug}`)}
                className="group cursor-pointer grid md:grid-cols-2 gap-10 rounded-2xl border border-white/[0.06] bg-[#0e0e10] overflow-hidden hover:border-white/[0.12] transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden bg-black">
                  {slug === "arc-agi-3" ? (
                    <div
                      className="w-full h-full flex items-center"
                      style={{ margin: "-16px 0 -40px" }}
                    >
                      <LevelChart />
                    </div>
                  ) : slug === "arcgentica" ? (
                    <ScatterChart />
                  ) : post.coverVideoKey ? (
                    <MuxVideo
                      assetKey={post.coverVideoKey}
                      className="w-full h-full object-cover scale-[1.08] group-hover:scale-[1.10] transition-transform duration-500"
                    />
                  ) : (
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover scale-[1.08] group-hover:scale-[1.10] transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="flex flex-col justify-center p-8 md:p-0 md:pr-10">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-xs font-ibm-plex-mono text-white/30 uppercase">
                      {post.date}
                    </span>
                    <span className="text-xs font-ibm-plex-mono text-[#7678E3] uppercase">
                      {post.categories[0]}
                    </span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] mb-4 group-hover:text-white/80 transition-colors duration-200">
                    {post.title}
                  </h2>
                  <p className="text-sm font-light text-white/40 leading-relaxed max-w-md mb-6">
                    {post.subtitle}
                  </p>
                  {slug === "arc-agi-3" && (
                    <div
                      className="p-4 mb-6 rounded-xl"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <p className="font-arizona-sans text-[14px] leading-relaxed text-white/80 mb-2">
                        Our implementation achieves a score of{" "}
                        <strong className="font-semibold">
                          36.08% with the Agentica SDK
                        </strong>{" "}
                        on the ARC-AGI-3 public evaluation set, outperforming
                        base model CoT baselines of 0.2% (Opus 4.6 Max) and 0.3%
                        (GPT 5.4 High).
                      </p>
                      <span className="font-arizona-sans text-[14px] text-white/80">
                        Check out the code on{" "}
                      </span>
                      <a
                        href="https://github.com/symbolica-ai/ARC-AGI-3-Agents"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-arizona-sans text-[14px] inline-flex items-center gap-1.5"
                        style={{
                          textDecoration: "none",
                          verticalAlign: "middle",
                        }}
                      >
                        <svg
                          height="14"
                          width="14"
                          viewBox="0 0 16 16"
                          fill="#fff"
                        >
                          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
                        </svg>
                        <span className="underline font-semibold text-white">
                          GitHub
                        </span>
                        <span className="underline text-white/60">
                          symbolica-ai/ARC-AGI-3-Agents
                        </span>
                      </a>
                    </div>
                  )}
                  {slug === "arcgentica" && (
                    <div
                      className="p-4 mb-6 rounded-xl"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <p className="font-arizona-sans text-[14px] leading-relaxed text-white/80 mb-2">
                        Our implementation achieves a score of{" "}
                        <strong className="font-semibold">
                          85.28% with Opus 4.6 (120k) High
                        </strong>{" "}
                        and increases the scores of GPT 5.2 (XHigh) and Opus 4.5
                        by 10 and 20 percentage points respectively.
                      </p>
                      <span className="font-arizona-sans text-[14px] text-white/80">
                        Check out the code on{" "}
                      </span>
                      <a
                        href="https://github.com/symbolica-ai/arcgentica"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-arizona-sans text-[14px] inline-flex items-center gap-1.5"
                        style={{
                          textDecoration: "none",
                          verticalAlign: "middle",
                        }}
                      >
                        <svg
                          height="14"
                          width="14"
                          viewBox="0 0 16 16"
                          fill="#fff"
                        >
                          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
                        </svg>
                        <span className="underline font-semibold text-white">
                          GitHub
                        </span>
                        <span className="underline text-white/60">
                          symbolica-ai/arcgentica
                        </span>
                      </a>
                    </div>
                  )}
                  <PillButton variant="primary" responsive>
                    Read
                  </PillButton>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <LandingFooter colorMode={isDark ? "dark" : "light"} noMargin />
    </div>
  );
}
